import React from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Box,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

const FilterSection = ({
  filter,
  setFilter,
  hostelList,
  selectedHostel,
  setSelectedHostel,
  handleSearch,
  setRows,
  setSearchTriggered,
}) => {
  const handleReset = () => {
    setFilter({
      month: "",
      billDate: "",
      customerName: "",
      mobileNumber: "",
      paymentStatus: "",
      serviceType: "",
    });

    setSelectedHostel("");
    setRows([]);
    setSearchTriggered(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        boxShadow: "0 4px 18px rgba(15, 23, 42, 0.05)",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#e3f2fd",
              color: "#1976d2",
            }}
          >
            <FilterAltOutlinedIcon fontSize="small" />
          </Box>

          <Box>
            <Typography
              fontWeight={700}
              fontSize={{ xs: 15, sm: 17 }}
              color="#1e293b"
            >
              Search & Filter
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Find guest billing records quickly
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* FILTER FIELDS */}
      <Grid container spacing={2}>
        {/* PROPERTY */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Property Name</InputLabel>

            <Select
              value={selectedHostel}
              label="Property Name"
              onChange={(e) => {
                setSelectedHostel(e.target.value);
                setSearchTriggered(false);
              }}
            >
              <MenuItem value="">
                All Properties
              </MenuItem>

              {hostelList.map((hostel) => (
                <MenuItem
                  key={hostel.id}
                  value={hostel.hostelName}
                >
                  {hostel.hostelName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* BILL MONTH */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="month"
            label="Bill Month"
            InputLabelProps={{
              shrink: true,
            }}
            value={filter.month}
            onChange={(e) => {
              setFilter({
                ...filter,
                month: e.target.value,
              });

              setSearchTriggered(false);
            }}
          />
        </Grid>

        {/* BILL DATE */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Bill Date"
            InputLabelProps={{
              shrink: true,
            }}
            value={filter.billDate}
            onChange={(e) =>
              setFilter({
                ...filter,
                billDate: e.target.value,
              })
            }
          />
        </Grid>

        {/* CUSTOMER NAME */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Customer Name"
            value={filter.customerName}
            onChange={(e) =>
              setFilter({
                ...filter,
                customerName: e.target.value,
              })
            }
          />
        </Grid>

        {/* MOBILE */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Mobile Number"
            inputProps={{
              maxLength: 10,
            }}
            value={filter.mobileNumber}
            onChange={(e) =>
              setFilter({
                ...filter,
                mobileNumber: e.target.value.replace(
                  /\D/g,
                  ""
                ),
              })
            }
          />
        </Grid>

        {/* PAYMENT STATUS */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Payment Status</InputLabel>

            <Select
              value={filter.paymentStatus}
              label="Payment Status"
              onChange={(e) =>
                setFilter({
                  ...filter,
                  paymentStatus: e.target.value,
                })
              }
            >
              <MenuItem value="">All Status</MenuItem>

              <MenuItem value="paid">
                Completed
              </MenuItem>

              <MenuItem value="partial">
                Partial
              </MenuItem>

              <MenuItem value="pending">
                Pending
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* SERVICE TYPE */}
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Service Type</InputLabel>

            <Select
              value={filter.serviceType}
              label="Service Type"
              onChange={(e) =>
                setFilter({
                  ...filter,
                  serviceType: e.target.value,
                })
              }
            >
              <MenuItem value="">
                All Services
              </MenuItem>

              <MenuItem value="mess">
                Mess Only
              </MenuItem>

              <MenuItem value="stay">
                Stay Only
              </MenuItem>

              <MenuItem value="both">
                Stay + Mess
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* BUTTONS */}
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: "100%",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{
                minHeight: 40,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
              }}
            >
              Search
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              sx={{
                minHeight: 40,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FilterSection;